import { Text } from '@mantine/core';
import React, { useMemo } from 'react';

interface MeasureMarkersProps {
    showBeatLines: boolean;
    measureWidth: number;
    measureMarkerHeight: number;
    trackLabelWidth: number;
    heightOfTracks: number;
    numberOfMeasures: number;
    isTabletOrSmaller: boolean;
    isPhoneOrSmaller: boolean;
}

type CSSPropertiesWithVars = React.CSSProperties & {
	'--measure-width'?: string;
	'--measure-marker-height'?: string;
	'--track-label-width'?: string;
    '--measure-line-height'?: string;
};

const MeasureMarkers: React.FC<MeasureMarkersProps> = React.memo(
    ({
        showBeatLines,
        measureWidth,
        measureMarkerHeight,
        trackLabelWidth,
        heightOfTracks,
        numberOfMeasures,
        isTabletOrSmaller,
        isPhoneOrSmaller
    }) => {
        // Memoize the markers so that they are recalculated only when their
        // dependent props change.
        const measureMarkers = useMemo(
            () => {
                return (
                    Array.from({ length: numberOfMeasures }, (_, index) => (
                        <div
                            key={`measure-marker-${index}`}
                            className={`measure-marker${index % 4 === 0 ? ' bold' : ''}`}
                        >
                            <Text
                                className="measure-number"
                                fz={isPhoneOrSmaller ? 11 : isTabletOrSmaller ? 14 : "md"}
                            >
                                {index + 1}
                            </Text>
                            <span className={`measure-line${index % 4 === 0 ? ' bold' : ''}`} />
                            {showBeatLines && (
                                <>
                                    <span className="beat-line beat-line-1" />
                                    <span className="beat-line beat-line-2" />
                                    <span className="beat-line beat-line-3" />
                                </>
                            )}
                        </div>
                    ))
                );
            },
            [numberOfMeasures, showBeatLines, isTabletOrSmaller, isPhoneOrSmaller]
        );
        const styleVars: CSSPropertiesWithVars = {
            '--measure-width': `${measureWidth}px`,
            '--measure-marker-height': `${measureMarkerHeight}px`,
            '--track-label-width': `${trackLabelWidth}px`,
            '--measure-line-height': `${heightOfTracks}px`,
        };

        return (
            <div
                className={`timeline-measure-marker-bar ${showBeatLines ? 'beat-lines' : ''
                    }`}
                style={styleVars}
            >
                {measureMarkers}
            </div>
        );
    }, (prevProps, nextProps) => {
        return (
            prevProps.numberOfMeasures === nextProps.numberOfMeasures &&
            prevProps.showBeatLines === nextProps.showBeatLines &&
            prevProps.measureWidth === nextProps.measureWidth &&
            prevProps.measureMarkerHeight === nextProps.measureMarkerHeight &&
            prevProps.trackLabelWidth === nextProps.trackLabelWidth &&
            prevProps.heightOfTracks === nextProps.heightOfTracks &&
            prevProps.isTabletOrSmaller === nextProps.isTabletOrSmaller &&
            prevProps.isPhoneOrSmaller === nextProps.isPhoneOrSmaller
        );
    }
);

export default MeasureMarkers;
